import { type DynamicImportComponentDetail } from 'hb-builder/types';
import { getEmailComponentsList } from 'hb-react/backoffice/components/EmailBuilder/utils/getEmailComponentsList';
import { getPageComponentsList } from 'hb-react/backoffice/components/PageBuilder/utils/getPageComponentsList';
import { BUILDER_DOCUMENT_TYPE } from 'hb-react/backoffice/types/builder/builderDocument';

export interface LoadedComponents<T> {
  [key: string]: T;
}

const getWebpackOptiPath = (isBackOffice?: boolean) =>
  isBackOffice ? 'BuilderComponents' : 'RenderComponents';

const getRequestedComponents = <T>(
  type: BUILDER_DOCUMENT_TYPE,
  isBackOffice?: boolean,
): Promise<LoadedComponents<T>> => {
  if (type === BUILDER_DOCUMENT_TYPE.PAGE) {
    return import(
      `hb-builder/components/${getWebpackOptiPath(isBackOffice)}/page/index`
    );
  }

  return import(
    `hb-builder/components/${getWebpackOptiPath(isBackOffice)}/email/index`
  );
};

const sortComponentDependingOnComponentsListOrder = <T>(
  componentsList: Array<DynamicImportComponentDetail>,
  requested: LoadedComponents<T>,
  shared: LoadedComponents<T>,
): LoadedComponents<T> =>
  componentsList.reduce((acc, { label, name }) => {
    const component = requested?.[name] || shared?.[name];

    if (component) {
      acc[label || name] = component;
    }

    return acc;
  }, {});

export const loadComponentsByType = async <ComponentType>({
  isBackOffice,
  isPageEvent = false,
  type = BUILDER_DOCUMENT_TYPE.PAGE,
}: {
  isBackOffice?: boolean;
  isPageEvent?: boolean;
  type: BUILDER_DOCUMENT_TYPE | undefined;
}) => {
  const sharedComponents: LoadedComponents<ComponentType> = await import(
    `hb-builder/components/${getWebpackOptiPath(isBackOffice)}/shared/index`
  );

  const requestedComponents: LoadedComponents<ComponentType> =
    await getRequestedComponents<ComponentType>(type, isBackOffice);

  const [shared, requested] = [sharedComponents, requestedComponents];

  const componentsList =
    type === 'page'
      ? getPageComponentsList({
          isPageEvent,
        })
      : getEmailComponentsList();

  return sortComponentDependingOnComponentsListOrder<ComponentType>(
    componentsList,
    requested,
    shared,
  );
};
